import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import BackrsLogoSmallFilled from '@backrs_ui/common-ui/src/assets/images/backrs-logo-filled.svg';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { useImageLoad } from '@backrs_ui/common-ui/src/hooks/useImageLoad';
import { DeepDive } from '@backrs_ui/common-ui/src/models/deepDive';

import { Avatar, Box, Button, Chip, Skeleton, SxProps, Theme, Typography } from '@mui/material';

export const DEEP_DIVE_CARD_IMAGE_HEIGHT_MAX = 200;
export const DEEP_DIVE_CARD_MAX_WIDTH = 300;
const STRINGS = appStrings.modules.deepDive.card;

type DeepDiveCardProps = Readonly<{
  deepDive: DeepDive;
  onCardClick?: (deepDive: DeepDive) => void;
  onCtaClick?: (deepDive: DeepDive) => void;
}>;

export const DeepDiveCard = ({ deepDive, onCardClick, onCtaClick }: DeepDiveCardProps) => {
  const handleCtaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCtaClick?.(deepDive);
  };

  const image = useImageLoad(deepDive.media);
  const showImage = deepDive.media && !image.imageError;
  const categoryBadges = deepDive.tags?.map((tag) => tag.name) ?? [];

  return (
    <Box sx={cardSx} onClick={() => onCardClick?.(deepDive)}>
      <Box sx={imageWrapSx}>
        {showImage ? (
          <>
            {!image.imageLoaded && (
              <Skeleton variant="rectangular" animation="wave" sx={imageSkeletonSx} />
            )}
            <Box
              component="img"
              src={deepDive.media}
              alt={deepDive.name}
              sx={coverImageWithLoadSx(image.imageLoaded)}
              {...image.imageProps}
            />
          </>
        ) : null}
        <Chip
          variant="badge"
          size="sm"
          label={STRINGS.backrsLabel}
          sx={backrsChipSx}
          icon={<Avatar src={BackrsLogoSmallFilled} sx={avatarSx} />}
        />
      </Box>
      <Box sx={contentSx}>
        <Box sx={textAndSupportingTextSx}>
          <Box sx={titleRowSx}>
            <Typography variant="text-lg-semibold" sx={titleSx}>
              {deepDive.name}
            </Typography>
            {deepDive?.price && (
              <Typography variant="text-md-medium" sx={priceSx}>
                ${deepDive.price}
              </Typography>
            )}
          </Box>
          <Box sx={descriptionBadgeSx}>
            <Typography variant="text-sm-regular" sx={descriptionSx}>
              {deepDive.nugget_description}
            </Typography>
            {categoryBadges.length > 0 && (
              <Box sx={badgesContainerSx}>
                {categoryBadges.map((badge) => (
                  <Box key={badge} sx={categoryBadgeSx}>
                    <Typography variant="text-xs-medium">{badge}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
        <Button
          id="deep-dive-schedule-btn"
          hierarchy="Secondary gray"
          size="lg"
          fullWidth
          onClick={handleCtaClick}
          sx={ctaButtonSx}
        >
          <AppIcon iconName="CalendarPlus01" diameter="20px" color="currentColor" />
          <Typography variant="text-sm-semibold">{STRINGS.ctaLabel}</Typography>
        </Button>
      </Box>
    </Box>
  );
};

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  overflow: 'hidden',
  width: '100%',
  height: '100%',
  maxWidth: DEEP_DIVE_CARD_MAX_WIDTH,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'all 0.3s ease-in-out',

  '&:hover': {
    borderColor: backrs.colors['Colors/Foreground/fg-primary'],
    transform: 'translate(-2px, -2px)',
    boxShadow: backrs.shadows['shadow-lg'],
  },

  '&:active': {
    transform: 'none !important',
    boxShadow: 'none !important',
    transition: 'all 0s !important',
  },
});

const imageWrapSx: SxProps<Theme> = ({ backrs }) => ({
  height: DEEP_DIVE_CARD_IMAGE_HEIGHT_MAX,
  minHeight: DEEP_DIVE_CARD_IMAGE_HEIGHT_MAX,
  backgroundColor: backrs.colors['Colors/Background/bg-brand-solid-hover'],
  position: 'relative',
  overflow: 'hidden',
  borderBottom: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const coverImageWithLoadSx = (loaded: boolean): SxProps<Theme> => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  opacity: loaded ? 1 : 0,
  transition: 'opacity 0.3s ease-in-out',
});

const imageSkeletonSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
};

const backrsChipSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-primary'],
  paddingLeft: backrs.spacing['spacing-none'],
  gap: backrs.spacing['spacing-lg'],
  position: 'absolute',
  bottom: backrs.spacing['spacing-xl'],
  left: backrs.spacing['spacing-xl'],
  backgroundColor: backrs.colors['Colors/Base/White'],
});

const avatarSx: SxProps<Theme> = {
  width: '16px',
  height: '16px',
};

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-2xl'],
  paddingX: backrs.spacing['spacing-xl'],
  paddingY: backrs.spacing['spacing-2xl'],
  width: '100%',
  flex: 1,
});

const textAndSupportingTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-xxs'],
  width: '100%',
});

const titleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  width: '100%',
  gap: backrs.spacing['spacing-md'],
});

const descriptionBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
  color: backrs.colors['Colors/Text/text-primary'],
  flex: 1,
});

const priceSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-secondary'],
  whiteSpace: 'nowrap',
  flexShrink: 0,
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: backrs.colors['Colors/Text/text-tertiary'],
  height: '40px',
  minHeight: '40px',
});

const badgesContainerSx: SxProps<Theme> = () => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '2px',
});

const categoryBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Brand/50'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
  color: backrs.colors['Brand/700'],
  borderRadius: '16px',
  paddingX: backrs.spacing['spacing-md'],
  paddingY: backrs.spacing['spacing-xxs'],
});

const ctaButtonSx: SxProps<Theme> = {
  mt: 'auto',
};
